import { useCallback } from 'react';
import { supabase, Database } from './supabase';

export type CreditsUsage = Database['public']['Tables']['credits_usage']['Row'];

export type SubscriptionType = 'free' | 'trial' | 'hobby' | 'creator' | 'ultimate' | 'admin' | null;

export const CREDITS_CONSTANTS = {
    CREDIT_GIFTS: {
      FREE_DAILY_GIFT: 2,
      FREE_SIGN_UP_GIFT: 3
    },
    CREDIT_LIMITS: {
      GLOBAL_LIMIT: 50000,
      FREE_LIMIT_NEW_USER: 10,
      FREE_LIMIT_MONTHLY: 10,
      FREE_TRIAL_LIMIT: 10,
      PAID_HOBBY_LIMIT: 100,
      PAID_CREATOR_LIMIT: 300,
    },
    CREDIT_COSTS: {
      COST_GAME_CREATE: 3,
      COST_GAME_EDIT: 1,
      COST_ART_2D: 1,
      COST_BG_REMOVE: 0.1,
      COST_ART_3D: 3,
      COST_VIDEO: 3,
      COST_VIDEO_BG_REMOVE: 1.0,
    },
    USER_FACING_CONVERSION: 10,
};

// Helper function to convert user-facing credit values to database values
export function toDBCredits(userFacingValue: number): number {
  return Math.round(userFacingValue * CREDITS_CONSTANTS.USER_FACING_CONVERSION);
}

// Helper function to convert database credit values to user-facing values
export function toUserFacingCredits(dbValue: number): number {
  return dbValue / CREDITS_CONSTANTS.USER_FACING_CONVERSION;
}

export interface CreditInfo {
  creditsUsed: number;
  creditsRemaining: number;
  totalCredits: number;
  nextResetDate: Date | null;
}

export interface OutOfCredits {
  is_limited_overall: boolean;
  game_create_limited: boolean;
  game_edit_limited: boolean;
  art_2d_limited: boolean;
  bg_remove_limited: boolean;
  art_3d_limited: boolean;
  animate_limited: boolean;
}

export const useCredits = () => {
  /**
   * Checks if a user is rate limited
   * @param userId - The ID of the user to check
   * @returns Object indicating if the user is limited overall and per feature
   */
  const getIsOutOfCredits = useCallback(async (userId: string): Promise<OutOfCredits> => {
    try {
      const { data: credits, error } = await supabase
        .from('credits_usage')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error checking credits:', error);
        return {
          is_limited_overall: false,
          game_create_limited: false,
          game_edit_limited: false,
          art_2d_limited: false,
          bg_remove_limited: false,
          art_3d_limited: false,
          animate_limited: false
        };
      }

      // If subscription is admin, always return false for all limits
      if (credits.subscription === 'admin') {
        return {
          is_limited_overall: false,
          game_create_limited: false,
          game_edit_limited: false,
          art_2d_limited: false,
          bg_remove_limited: false,
          art_3d_limited: false,
          animate_limited: false
        };
      }

      // Calculate total available credits based on subscription type
      let totalAvailableCredits = 0;
      
      // For free or null subscription, only check free + flex credits
      if (credits.subscription === 'free' || !credits.subscription) {
        totalAvailableCredits = 
          (credits.free_credits_received_this_month - credits.free_credits_used_this_month) + 
          (credits.flex_credits_available - credits.flex_credits_used_this_month);
      } else {
        // For paid subscriptions, use all credit types
        totalAvailableCredits = 
          (credits.free_credits_received_this_month - credits.free_credits_used_this_month) +
          (credits.paid_credits_received_this_month - credits.paid_credits_used_this_month) +
          (credits.flex_credits_available - credits.flex_credits_used_this_month);
      }
      
      // Convert database credits to user-facing credits for comparison with costs
      const userFacingTotalAvailableCredits = toUserFacingCredits(totalAvailableCredits);
      
      // Check if there are enough credits for each feature
      const gameCreateLimited = userFacingTotalAvailableCredits < CREDITS_CONSTANTS.CREDIT_COSTS.COST_GAME_CREATE;
      const gameEditLimited = userFacingTotalAvailableCredits < CREDITS_CONSTANTS.CREDIT_COSTS.COST_GAME_EDIT;
      const art2dLimited = userFacingTotalAvailableCredits < CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_2D;
      const bgRemoveLimited = userFacingTotalAvailableCredits < CREDITS_CONSTANTS.CREDIT_COSTS.COST_BG_REMOVE;
      const art3dLimited = userFacingTotalAvailableCredits < CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_3D;
      const animateLimited = userFacingTotalAvailableCredits < CREDITS_CONSTANTS.CREDIT_COSTS.COST_VIDEO;
      
      // Overall limited if all features are limited
      const isLimitedOverall = gameCreateLimited && gameEditLimited && art2dLimited && bgRemoveLimited && art3dLimited && animateLimited;

      return {
        is_limited_overall: isLimitedOverall,
        game_create_limited: gameCreateLimited,
        game_edit_limited: gameEditLimited,
        art_2d_limited: art2dLimited,
        bg_remove_limited: bgRemoveLimited,
        art_3d_limited: art3dLimited,
        animate_limited: animateLimited
      };
    } catch (error) {
      console.error('Exception checking rate limit:', error);
      return {
        is_limited_overall: false,
        game_create_limited: false,
        game_edit_limited: false,
        art_2d_limited: false,
        bg_remove_limited: false,
        art_3d_limited: false,
        animate_limited: false
      };
    }
  }, []);

  /**
   * Gets the credit information for a user
   * @param userId - The ID of the user
   * @returns Object containing credits used and credits remaining
   */
  const getCredits = useCallback(async (userId: string): Promise<CreditInfo> => {
    try {
      const { data: credits, error } = await supabase
        .from('credits_usage')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error getting credits:', error);
        return { creditsUsed: 0, creditsRemaining: 0, totalCredits: 0, nextResetDate: null };
      }

      // Calculate total credits used in DB format
      const dbCreditsUsed = 
        credits.free_credits_used_this_month + 
        credits.paid_credits_used_this_month + 
        credits.flex_credits_used_this_month;
      
      // Calculate total credits received in DB format
      const dbTotalCredits = 
        credits.free_credits_received_this_month + 
        credits.paid_credits_received_this_month + 
        credits.flex_credits_available;
      
      // Calculate remaining credits in DB format
      const dbCreditsRemaining = dbTotalCredits - dbCreditsUsed;
      
      // Convert to user-facing values
      const creditsUsed = toUserFacingCredits(dbCreditsUsed);
      const totalCredits = toUserFacingCredits(dbTotalCredits);
      const creditsRemaining = toUserFacingCredits(dbCreditsRemaining);
      
      // Calculate next reset date (last reset + 1 month)
      let nextResetDate: Date | null = null;
      if (credits.last_monthly_reset_at) {
        nextResetDate = new Date(credits.last_monthly_reset_at);
        nextResetDate.setMonth(nextResetDate.getMonth() + 1);
      }

      return {
        creditsUsed,
        creditsRemaining,
        totalCredits,
        nextResetDate
      };
    } catch (error) {
      console.error('Exception getting credits:', error);
      return { creditsUsed: 0, creditsRemaining: 0, totalCredits: 0, nextResetDate: null };
    }
  }, []);

  /**
   * Gets the subscription type for a user
   * @param userId - The ID of the user
   * @returns The subscription type ('free', 'hobby', or null)
   */
  const getSubscription = useCallback(async (userId: string): Promise<SubscriptionType> => {
    try {
      const { data: credits, error } = await supabase
        .from('credits_usage')
        .select('subscription')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error getting subscription:', error);
        return 'free'; // Default to free on error
      }
      
      const plan = (credits?.subscription as SubscriptionType) || 'free';
      // console.log('Plan:', plan);

      return plan;
    } catch (error) {
      console.error('Exception getting subscription:', error);
      return 'free'; // Default to free on exception
    }
  }, []);

  /**
   * Checks if a user is eligible for a free daily gift message
   * @param userId - The ID of the user to check
   * @param isHardPaywallActive - Feature flag state from client-side config
   * @returns Boolean indicating if the user is eligible for a free daily gift
   */
  const checkShowFreeDailyGiftMessage = useCallback(async (userId: string, isHardPaywallActive: boolean): Promise<boolean> => {
    try {
      const { data: credits, error } = await supabase
        .from('credits_usage')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('Error checking daily gift eligibility:', error);
        return false;
      }

      // If subscription is admin, don't show gift message
      if (credits.subscription === 'admin') {
        return false;
      }
      
      // If hard paywall is active and user is free/unsubscribed, they are not eligible for daily gift
      if (isHardPaywallActive && (credits.subscription === null || credits.subscription === 'free')) {
        return false;
      }

      // Check time eligibility for daily credits (only if not blocked by paywall)
      const lastDailyGiftDate = new Date(credits.last_daily_gift_at);
      const currentDate = new Date();
      const oneDay = 24 * 60 * 60 * 1000;
      const isEligibleForDailyGift = currentDate.getTime() - lastDailyGiftDate.getTime() >= oneDay;
      // console.log('is eligible for daily gift', isEligibleForDailyGift);
      
      if (isEligibleForDailyGift) {
        // Check credit limit eligibility (only if time is right and not blocked by paywall)
        if (credits.is_new_sign_up && 
            credits.free_credits_received_this_month < toDBCredits(CREDITS_CONSTANTS.CREDIT_LIMITS.FREE_LIMIT_NEW_USER)) {
          return true;
        } else if (!credits.is_new_sign_up && 
            credits.free_credits_received_this_month < toDBCredits(CREDITS_CONSTANTS.CREDIT_LIMITS.FREE_LIMIT_MONTHLY)) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Exception checking daily gift eligibility:', error);
      return false;
    }
  }, []);

  return {
    getIsOutOfCredits,
    getCredits,
    getSubscription,
    checkShowFreeDailyGiftMessage,
  };
};